/**
 * CourseCompletionCelebration — Full-screen amber-navy modal that fires
 * once when a learner mints their first certificate for a course. Shows
 * their name + cert number + a big "View certificate" CTA. Confetti is a
 * lightweight CSS burst (no external lib needed).
 */
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { GraduationCap, ArrowRight, X, FileText } from 'lucide-react';
import { Button } from './ui/button';

const Confetti = () => {
  const pieces = Array.from({ length: 40 });
  const colours = ['#F5B841', '#1e63c9', '#0b1e3f', '#fff', '#6ee7b7'];
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
      {pieces.map((_, i) => {
        const left = Math.random() * 100;
        const delay = Math.random() * 1.4;
        const dur = 1.6 + Math.random() * 1.4;
        const size = 6 + Math.random() * 6;
        const rot = Math.random() * 360;
        const bg = colours[i % colours.length];
        return (
          <span
            key={i}
            style={{
              position: 'absolute',
              left: `${left}%`,
              top: '-20px',
              width: `${size}px`,
              height: `${size * 0.4}px`,
              background: bg,
              transform: `rotate(${rot}deg)`,
              animation: `confetti-fall ${dur}s ${delay}s linear forwards`,
              opacity: 0.9,
            }}
          />
        );
      })}
      <style>{`
        @keyframes confetti-fall {
          0% { transform: translateY(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(110vh) rotate(720deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
};

const CourseCompletionCelebration = ({ certificate, onClose }) => {
  const navigate = useNavigate();
  const videoRef = React.useRef(null);

  useEffect(() => {
    // Block background scroll while modal is open
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Try to autoplay with sound on mount; silent fallback on browser block.
  useEffect(() => {
    if (!certificate || !videoRef.current) return;
    const v = videoRef.current;
    v.muted = false; v.volume = 1;
    const p = v.play();
    if (p && typeof p.then === 'function') {
      p.catch(() => { v.muted = true; v.play().catch(() => {}); });
    }
  }, [certificate]);

  if (!certificate) return null;

  const viewCert = () => {
    onClose?.();
    navigate(`/certificate/${certificate.cert_number}`);
  };

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-navy/85 backdrop-blur-sm animate-in fade-in duration-500"
      role="dialog"
      aria-label="Course completion celebration"
      data-testid="course-completion-celebration"
    >
      <Confetti />
      <div className="relative max-w-lg w-[92vw] pro-card bg-card p-8 sm:p-10 text-center animate-in zoom-in-95 slide-in-from-bottom-4 duration-500 border-2 border-primary/40">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 h-9 w-9 rounded-full hover:bg-primary/10 inline-flex items-center justify-center text-foreground/60 hover:text-foreground transition"
          data-testid="course-completion-close"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        <div
          className="h-40 sm:h-48 mx-auto rounded-2xl overflow-hidden shadow-lg border border-primary/30 bg-navy mb-5"
          data-testid="celebration-video-wrap"
        >
          <video
            ref={videoRef}
            className="w-full h-full object-cover"
            src="/certificate-awarded.mp4"
            autoPlay
            playsInline
            data-testid="celebration-video"
          />
        </div>

        <div className="eyebrow text-primary">Certificate earned</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-foreground tracking-tight mt-3">
          You just crushed
          <br />
          <span className="text-amber-brand">{certificate.course_title}.</span>
        </h1>

        <p className="mt-5 text-foreground/70 leading-relaxed">
          A verifiable certificate has been minted with your name{certificate.holder_name ? <> — <span className="font-extrabold text-foreground">{certificate.holder_name}</span></> : ''}. Certificate number:
        </p>
        <div
          className="mt-3 font-mono text-lg font-extrabold text-primary bg-primary/5 border border-primary/20 rounded-xl py-3 px-4 inline-block"
          data-testid="celebration-cert-number"
        >
          {certificate.cert_number}
        </div>

        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <Button
            size="lg"
            onClick={viewCert}
            className="rounded-full px-7 py-6 font-bold bg-primary text-primary-foreground hover:bg-primary/90"
            data-testid="celebration-view-cert-btn"
          >
            View certificate <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
          <a
            href={`/api/verify/${certificate.cert_number}/certificate.pdf`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center rounded-full px-7 py-6 font-bold border border-input hover:bg-accent text-sm"
            data-testid="celebration-pdf-btn"
            title="Download a shareable PDF of your certificate"
          >
            <FileText className="w-4 h-4 mr-2" /> Download PDF
          </a>
          <Button
            size="lg"
            variant="outline"
            onClick={onClose}
            className="rounded-full px-7 py-6 font-bold"
            data-testid="celebration-keep-learning-btn"
          >
            Keep learning
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CourseCompletionCelebration;
